import { Alert, Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { styled } from 'enova-frontend-components';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiRequestError, getRegistrationSubmitErrorTextKeys } from '../services/api';

import { ErrorSupportDetails } from './errorSupportDetails';

interface ErrorProps {
  titleKey?: string;
  descriptionKey?: string;
  onRetry?: () => void;
  retryLabelKey?: string;
  traceId?: string | null;
  // When passed, the title/description default to the error code's text and the
  // support block copies rich details (step, route, errorCode, status).
  error?: ApiRequestError | null;
  registrationId?: string;
  children?: ReactNode;
}

const ErrorAlert = styled(Alert)`
  display: flex;
  flex-direction: column;
  gap: var(--ds-size-4);
  margin-bottom: var(--ds-size-4);
`;

const supportSectionStyle = {
  marginTop: `var(--ds-size-4)`
} as const;

export const ErrorBox: FC<ErrorProps> = ({
  titleKey,
  descriptionKey,
  onRetry,
  retryLabelKey = 'generalError.retry',
  traceId,
  error,
  registrationId,
  children
}) => {
  const { t } = useTranslation();

  const derived = error ? getRegistrationSubmitErrorTextKeys(error.errorCode) : undefined;
  const resolvedTitleKey = titleKey ?? derived?.titleKey ?? 'generalError.title';
  // Empty string is honoured (some sites omit the description and rely on children).
  const resolvedDescriptionKey = descriptionKey ?? derived?.descriptionKey ?? 'generalError.description';
  const resolvedTraceId = traceId ?? error?.traceId ?? null;

  // Form call sites mount <ErrorBox error={submitError} /> unconditionally, so render
  // nothing when there is neither an error nor any caller-supplied copy to show.
  if (!error && !titleKey && !descriptionKey && !children) {
    return null;
  }

  return (
    <ErrorAlert data-color="danger">
      <Heading data-size="xs" level={2}>
        {t(resolvedTitleKey)}
      </Heading>
      {resolvedDescriptionKey && <Paragraph>{t(resolvedDescriptionKey)}</Paragraph>}
      {children}
      {onRetry && (
        <div>
          <Button variant="secondary" onClick={onRetry}>{t(retryLabelKey)}</Button>
        </div>
      )}

      <ErrorSupportDetails style={supportSectionStyle} error={error} traceId={resolvedTraceId} registrationId={registrationId} />
    </ErrorAlert>
  );
};
